// src/api/apiClient.ts
import axios, { AxiosInstance, AxiosError } from "axios";
import type { InternalAxiosRequestConfig } from "axios";

// src/api/apiClient.ts (top part)
const rawApiUrl = process.env.REACT_APP_API_URL ?? "https://vidyasamruddhi.co.in";
const rawPrefix = process.env.REACT_APP_API_PREFIX ?? "/api";

// remove trailing slash from URL and ensure prefix starts with single slash
const API_URL = rawApiUrl.replace(/\/+$/, "");
const API_PREFIX = rawPrefix.startsWith("/") ? rawPrefix : `/${rawPrefix}`;

const BASE = API_URL + API_PREFIX;


const apiClient: AxiosInstance = axios.create({
    baseURL: BASE,
    timeout: 30000,
    headers: {
        "Accept": "application/json",
    },
});

// helper: read auth from sessionStorage
function getAuth(): { accessToken?: string; refreshToken?: string; user?: any } | null {
    try {
        const raw = sessionStorage.getItem("vs_auth");
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function setAuth(obj: any) {
    sessionStorage.setItem("vs_auth", JSON.stringify(obj));
}

// Refresh queue state
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;

/**
 * Refresh tokens using refresh endpoint.
 * Uses a plain axios instance (no interceptors) to avoid loops.
 */
async function doRefresh(refreshToken: string) {
    const plain = axios.create({ baseURL: BASE, timeout: 20000 });
    // endpoint path - adjust if your backend expects a different route
    // you showed: http://localhost:8080/learnex/api/auth/refresh -> relative likely /auth/refresh
    const refreshPath = "/auth/refresh";
    const resp = await plain.post(refreshPath, { refreshToken });
    return resp.data;
}

// ----- request interceptor: attach access token
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const auth = getAuth();
    if (auth?.accessToken) {
        // ensure headers object exists
        config.headers = config.headers ?? {};
        // axios v1 headers typing: set Authorization as string
        (config.headers as any)["Authorization"] = `Bearer ${auth.accessToken}`;
    }
    return config;
}, (error) => Promise.reject(error));

// ----- response interceptor: handle token expiry and retry
apiClient.interceptors.response.use(
    (resp) => resp,
    async (error: AxiosError & { config?: InternalAxiosRequestConfig & { _retry?: boolean } }) => {
        const originalConfig = error?.config;
        const status = error?.response?.status;

        // If no config or already retried -> reject
        if (!originalConfig) return Promise.reject(error);

        // treat 498 OR 401 as expired token
        if ((status === 498 || status === 401) && !originalConfig._retry) {
            originalConfig._retry = true;

            const auth = getAuth();
            const refreshToken = auth?.refreshToken;

            // Case 1: No refresh token available to try
            if (!refreshToken) {
                sessionStorage.removeItem("vs_auth");
                window.location.href = '/login'; // Force redirect to login
                return Promise.reject(error);
            }

            try {
                // ... existing refresh logic ...
                if (isRefreshing && refreshPromise) {
                    await refreshPromise;
                } else {
                    isRefreshing = true;
                    refreshPromise = (async () => {
                        try {
                            const newTokens = await doRefresh(refreshToken);
                            // ... merging tokens logic ...
                            const merged = {
                                ...auth,
                                accessToken: newTokens.accessToken ?? newTokens.access_token ?? auth.accessToken,
                                refreshToken: newTokens.refreshToken ?? newTokens.refresh_token ?? auth.refreshToken,
                                user: newTokens.user ?? auth.user,
                            };
                            setAuth(merged);
                            return merged;
                        } finally {
                            isRefreshing = false;
                            refreshPromise = null;
                        }
                    })();
                    await refreshPromise;
                }

                // retry original request
                return apiClient(originalConfig);
            } catch (refreshErr) {
                // Case 2: Refresh failed (token invalid/expired)
                try { sessionStorage.removeItem("vs_auth"); } catch { }
                window.location.href = '/login'; // Force redirect to login
                return Promise.reject(refreshErr);
            }
        }

        return Promise.reject(error);
    }
);

export default apiClient;
